import React, { useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import {
  UserIcon,
  IdentificationIcon,
  PhoneIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { Appointment, Case } from '../../types';
import { t } from '../../utils/translations';
import ConfirmDialog from '../common/ConfirmDialog';
import ExpandCollapse from '../common/ExpandCollapse';
import Button, { IconButton } from '../ui/Button';
import { actionIcon } from '../ui/actionIcons';
import { useAuth } from '../../contexts/AuthContext';
import { logConsentUploaded } from '../../utils/activityLogger';
import {
  CONSENT_ALLOWED_TYPES,
  uploadCaseConsent,
  validateConsentFile,
} from '../../utils/consentUpload';
import { formatNextAppointmentChipLabel, formatTimeAgo } from '../Dashboard/dashboardUtils';
import SessionRoad from '../Dashboard/SessionRoad';
import { useCaseAccess } from '../../hooks/useCaseAccess';
import { useSessionQuestions } from '../../contexts/SessionQuestionsContext';
import {
  getCaseDisplayId,
  getInitials,
  getStatusLabel,
  getStatusBadgeClass,
  getAvatarColorClass,
  translateSex,
  translateCivilStatus,
  translateIssueType,
  getIssueTypeBadgeClass,
  translateReferralSource,
  needsCaseReassignment,
} from './casesUtils';

interface CaseListCardProps {
  caseItem: Case;
  latestNote: string;
  reportsCount: number;
  /** Session numbers with a tablou already, for the session road's "Vezi tabloul" CTA. */
  tabloulSessions?: Set<number>;
  nextAppointment?: Appointment;
  /** Upcoming appointments for this case, keyed by session number. */
  appointmentBySession?: Record<number, Appointment>;
  pulseAppointment?: boolean;
  /** A past meeting has no session report yet. */
  needsReport?: boolean;
  /**
   * Still asking for this session's tablou. Decided by the caller, not read off the case: the ask
   * goes quiet once the next session is close enough to prepare for, and it is not the
   * apprentice's to answer on a shadowed case.
   */
  askTablou?: boolean;
  /** How long the agreed meeting cadence has been exceeded, when it has. */
  frequencyOverdueBy?: string | null;
  onOpenNotes: () => void;
  onAddNote: () => void;
  onOpenAddReport?: () => void;
  onOpenReports?: () => void;
  onOpenReportSession?: (session: number) => void;
  onSchedule?: (session?: number) => void;
  onOpenTimeline: () => void;
  /** Opens the same briefing "Pregătește sesiunea" uses — last session, tablou, tema. */
  onDeepen?: () => void;
  onCloseCase?: () => void;
  onEdit?: () => void;
  onReassign?: () => void;
  onEditDescription?: () => void;
  onOpenDescription: () => void;
  onOpenAppointment?: () => void;
  onDelete?: () => void;
  /** Hide sensitive meeting-notes preview (e.g. admin case management). */
  hideMeetingNotes?: boolean;
  /** Short case id (#C-xxxx) — shown in admin only. */
  showCaseId?: boolean;
  /** Blur description and restrict session-report actions (admin role). */
  blurSensitiveContent?: boolean;
  /** Off where session counts are not loaded (admin case management). */
  showSessionPath?: boolean;
  /** Show the reports entry even when this card did not receive a report count. */
  alwaysShowReports?: boolean;
  /**
   * Name the counsellor on the card. Needed wherever the list spans the whole team
   * (admin case management), where ownership is otherwise only reachable through the filter.
   */
  showAssignedCounselor?: boolean;
}

const zoneLabelClass =
  'text-xs font-semibold uppercase tracking-wide text-slate-500';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** „3 August”, with the year only when it is not the current one. */
function formatLongDate(date: Date): string {
  const month = date.toLocaleDateString('ro-RO', { month: 'long' });
  const monthLabel = month.charAt(0).toUpperCase() + month.slice(1);
  const year =
    date.getFullYear() === new Date().getFullYear() ? '' : ` ${date.getFullYear()}`;
  return `${date.getDate()} ${monthLabel}${year}`;
}

/**
 * Relative wording only while it stays relative: past one week `formatTimeAgo`
 * falls back to a calendar date, which the long date already covers.
 */
function formatLastMeetingLabel(date?: Date | null): string | null {
  if (!date) return null;
  const longDate = formatLongDate(date);
  if (Date.now() - date.getTime() >= WEEK_MS) return longDate;
  return `${formatTimeAgo(date)} · ${longDate}`;
}

const ZoneLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={zoneLabelClass}>{children}</p>
);

const DetailCard: React.FC<{
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon: Icon, action, children }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-3.5">
    <div className="flex items-center gap-2">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-100">
        <Icon className="h-3.5 w-3.5 text-slate-500" />
      </span>
      <h3 className={zoneLabelClass}>{title}</h3>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    <div className="mt-2.5">{children}</div>
  </section>
);

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1.5 first:pt-0 last:border-0 last:pb-0">
    <span className="shrink-0 text-xs text-slate-400">{label}</span>
    <span className="min-w-0 break-words text-right text-sm text-slate-700">
      {children}
    </span>
  </div>
);

const CaseListCard: React.FC<CaseListCardProps> = ({
  caseItem,
  latestNote,
  reportsCount,
  tabloulSessions,
  nextAppointment,
  appointmentBySession,
  pulseAppointment = false,
  needsReport = false,
  askTablou = false,
  frequencyOverdueBy = null,
  onOpenNotes,
  onAddNote,
  onOpenAddReport,
  onOpenReports,
  onOpenReportSession,
  onSchedule: onScheduleProp,
  onOpenTimeline,
  onDeepen,
  onCloseCase: onCloseCaseProp,
  onEdit: onEditProp,
  onReassign: onReassignProp,
  onEditDescription: onEditDescriptionProp,
  onOpenDescription,
  onOpenAppointment,
  onDelete,
  hideMeetingNotes = false,
  showCaseId = false,
  blurSensitiveContent = false,
  showSessionPath = true,
  alwaysShowReports = false,
  showAssignedCounselor = false,
}) => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [consentConfirmOpen, setConsentConfirmOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [localConsentAttached, setLocalConsentAttached] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const access = useCaseAccess();
  const { openTablou } = useSessionQuestions();
  // Shadowing is read-only apart from the apprentice's own notes, so every
  // write entry point is withheld here rather than at each call site.
  const canEditCase = access.canEditCase(caseItem.id);
  const onSchedule = canEditCase ? onScheduleProp : undefined;
  const onCloseCase = canEditCase ? onCloseCaseProp : undefined;
  const onEdit = canEditCase ? onEditProp : undefined;
  const onReassign = canEditCase ? onReassignProp : undefined;
  const onEditDescription = canEditCase ? onEditDescriptionProp : undefined;
  const shadowedMentorName = access.mentorNameFor(caseItem.id);
  const observers = access.observersFor(caseItem.id);
  const canSeeConsent = access.canSeeConsent(caseItem.id);
  // This button opens the note composer directly, bypassing the MeetingNotes
  // dialog, so it needs its own check.
  const canAddNote = access.canAddNote(caseItem.id);
  const isActive = caseItem.status === 'active';
  const canAddReport =
    Boolean(onOpenAddReport) && isActive && !blurSensitiveContent && canEditCase;
  const showReports =
    Boolean(onOpenReports) && (alwaysShowReports || reportsCount > 0 || isActive);
  const description = caseItem.description || '';
  const isTruncated = description.length > 150;
  const consentAttached =
    localConsentAttached || caseItem.consentAttached === true;
  const openNextStep = caseItem.openNextStep ?? null;
  const showConsentAlert = !consentAttached && !blurSensitiveContent && canSeeConsent;
  const pendingClarifySession = askTablou ? caseItem.pendingClarifySession ?? null : null;
  const hasAlerts =
    showConsentAlert || Boolean(frequencyOverdueBy) || needsReport || pendingClarifySession !== null;
  const lastMeetingLabel = formatLastMeetingLabel(caseItem.lastMeetingDate);

  const acceptAttr = CONSENT_ALLOWED_TYPES.join(',');

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const error = validateConsentFile(file);
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      setPendingFile(null);
      return;
    }
    setPendingFile(file);
  };

  const handleConsentConfirm = async () => {
    if (!pendingFile || !currentUser?.id || uploadLoading) return;
    try {
      setUploadLoading(true);
      await uploadCaseConsent({
        caseItem,
        file: pendingFile,
        userId: currentUser.id,
        userName: currentUser.fullName || '',
      });
      await logConsentUploaded(
        caseItem.id,
        caseItem.title,
        currentUser.id,
        currentUser.fullName || ''
      );
      setLocalConsentAttached(true);
      setPendingFile(null);
      setConsentConfirmOpen(false);
      setSnackbar({
        open: true,
        message: t.cases.consentUploadSuccess,
        severity: 'success',
      });
    } catch (err) {
      console.error('Consent upload failed:', err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : t.cases.consentUploadError,
        severity: 'error',
      });
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <>
      {/* The session-road bubble overflows the card, so the hovered card has to
          win over its siblings while staying below the sticky topbar (z-30). */}
      <article
        id={`case-${caseItem.id}`}
        className="relative overflow-visible rounded-xl border border-slate-200 bg-white shadow-sm transition duration-200 ease-out hover:z-10 hover:shadow-md focus-within:z-10"
      >
        <div className="p-4 sm:p-5">
          <div className="flex flex-wrap items-start gap-x-3 gap-y-2.5">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${getAvatarColorClass(caseItem.counseledName)}`}
            >
              {getInitials(caseItem.counseledName)}
            </div>

            <div className="min-w-0 flex-1">
              {showCaseId && (
                <p className="text-xs text-slate-400">{getCaseDisplayId(caseItem)}</p>
              )}
              <h2 className="truncate text-lg font-semibold text-slate-900">
                {caseItem.counseledName}
              </h2>
              {caseItem.phoneNumber ? (
                <a
                  href={`tel:${caseItem.phoneNumber.replace(/\s+/g, '')}`}
                  className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-brand-700"
                >
                  <PhoneIcon className="h-3.5 w-3.5 shrink-0" />
                  {caseItem.phoneNumber}
                </a>
              ) : (
                <p className="truncate text-sm text-slate-500">{caseItem.title}</p>
              )}
              {showAssignedCounselor && (
                <div className="mt-1.5">
                  <span className="inline-flex max-w-full rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-800">
                    <span className="truncate">
                      {t.cases.assignedCounselorChip.replace(
                        '{name}',
                        caseItem.assignedCounselorName || t.cases.unassignedBadge
                      )}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {onDeepen && (
              // Mobile only — the same action sits as a labeled button in the row below on
              // desktop, where there is room for it. On a narrow card this is the one corner
              // still empty on the name's own line, rather than one more entry competing for
              // space in the already-wrapping action row further down.
              <IconButton
                icon={actionIcon.deepen}
                label={t.cases.cockpit.deepenCta}
                onClick={onDeepen}
                size="md"
                className="shrink-0 sm:hidden motion-safe:animate-deepen-glow"
              />
            )}

            <div className="flex w-full flex-wrap gap-1.5 sm:w-auto sm:justify-end">
              {shadowedMentorName && (
                <span className="rounded-full bg-fuchsia-100 px-2.5 py-0.5 text-xs font-medium text-fuchsia-800">
                  {t.meetingNotes.mentorCaseBadge.replace(
                    '{{name}}',
                    shadowedMentorName
                  )}
                </span>
              )}
              {observers.length > 0 && (
                <span className="rounded-full bg-pink-50 px-2.5 py-0.5 text-xs font-medium text-pink-700">
                  {t.meetingNotes.observerBadge.replace(
                    '{{names}}',
                    observers.join(', ')
                  )}
                </span>
              )}
              {caseItem.priority === 'high' && (
                <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                  {t.cases.priorityBadge}
                </span>
              )}
              {caseItem.issueTypes.map((issueType) => (
                <span
                  key={issueType}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getIssueTypeBadgeClass(issueType)}`}
                >
                  {translateIssueType(issueType)}
                </span>
              ))}
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(caseItem.status)}`}
              >
                {getStatusLabel(caseItem.status)}
              </span>
            </div>
          </div>

          {caseItem.assignmentStatus === 'pending' &&
            caseItem.proposedCounselorName && (
              <div className="mt-2.5">
                <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  {t.cases.proposalBadge.replace(
                    '{name}',
                    caseItem.proposedCounselorName
                  )}
                </span>
              </div>
            )}
          {onReassign && needsCaseReassignment(caseItem) && (
            <div className="mt-2.5">
              <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                {t.cases.unassignedBadge}
              </span>
            </div>
          )}

          <div className="my-4 h-px bg-slate-100" />

          <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,0.85fr)]">
            {/* Where the case stands */}
            <div className="order-2 min-w-0 xl:order-none">
              {showSessionPath && (
                <>
                  <ZoneLabel>{t.cases.cockpit.sessionPath}</ZoneLabel>
                  <div className="mt-2.5">
                    <SessionRoad
                      reportCount={reportsCount}
                      nextAppointment={nextAppointment}
                      appointmentBySession={appointmentBySession}
                      onAddReport={canAddReport ? onOpenAddReport : undefined}
                      onOpenReport={onOpenReportSession}
                      onSchedule={onSchedule}
                      tabloulSessions={tabloulSessions}
                      onOpenTablou={(session) => openTablou({ caseId: caseItem.id, sessionNumber: session })}
                    />
                  </div>
                </>
              )}

              <div className={showSessionPath ? 'mt-4' : ''}>
                <ZoneLabel>{t.cases.cockpit.lastMeeting}</ZoneLabel>
                {lastMeetingLabel ? (
                  <p
                    className={`mt-1 text-sm ${
                      frequencyOverdueBy
                        ? 'font-medium text-orange-700'
                        : 'text-slate-600'
                    }`}
                  >
                    {lastMeetingLabel}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-slate-400">
                    {t.cases.cockpit.noMeetingYet}
                  </p>
                )}
              </div>
            </div>

            {/* What was worked on and what comes next */}
            <div className="order-3 min-w-0 xl:order-none">
              <ZoneLabel>{t.cases.cockpit.lastTheme}</ZoneLabel>
              {caseItem.lastTheme ? (
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  {caseItem.lastTheme}
                </p>
              ) : (
                <p className="mt-1 text-sm text-slate-400">
                  {t.cases.cockpit.noTheme}
                </p>
              )}

              <div className="mt-4">
                {openNextStep ? (
                  <>
                    <ZoneLabel>
                      {t.cases.cockpit.nextStep} ·{' '}
                      {t.sessionReports.wizard.nextStepTypes[openNextStep.type]}
                    </ZoneLabel>
                    <div className="mt-1.5 rounded-lg bg-slate-50 px-3 py-2.5">
                      <p className="text-sm leading-relaxed text-slate-700">
                        {openNextStep.description}
                      </p>
                      {openNextStep.passage && (
                        <p className="mt-1.5 text-xs text-slate-500">
                          {t.cases.cockpit.nextStepPassage}:{' '}
                          {openNextStep.passage}
                        </p>
                      )}
                      {openNextStep.fromSessionNumber !== null && (
                        <p className="mt-1 text-xs text-slate-400">
                          {t.cases.cockpit.nextStepFromSession.replace(
                            '{n}',
                            String(openNextStep.fromSessionNumber)
                          )}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <ZoneLabel>{t.cases.cockpit.nextStep}</ZoneLabel>
                    <p className="mt-1 text-sm text-slate-400">
                      {t.cases.cockpit.noNextStep}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Scheduling and what blocks the case */}
            <div className="order-1 min-w-0 lg:col-span-2 xl:order-none xl:col-span-1">
              <ZoneLabel>{t.cases.cockpit.nextAppointment}</ZoneLabel>
              {nextAppointment ? (
                <button
                  type="button"
                  onClick={onOpenAppointment}
                  className={`${
                    pulseAppointment ? 'animate-schedule-pulse ' : ''
                  }mt-1.5 w-full rounded-lg bg-brand-600 px-3 py-2.5 text-left transition hover:bg-brand-700`}
                  title={t.dashboard.upcomingAppointments}
                >
                  <span className="block text-sm font-semibold text-white">
                    {formatNextAppointmentChipLabel(nextAppointment)}
                  </span>
                  {nextAppointment.room && (
                    <span className="block text-xs text-white/80">
                      {nextAppointment.room}
                    </span>
                  )}
                </button>
              ) : (
                <div className="mt-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2.5">
                  <p className="text-sm text-slate-400">
                    {t.cases.cockpit.noAppointment}
                  </p>
                </div>
              )}

              {hasAlerts && (
                <div className="mt-3 space-y-2">
                  {frequencyOverdueBy && (
                    <div className="flex items-center gap-2 rounded-lg border-l-2 border-orange-500 bg-orange-50 px-2.5 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-orange-900">
                          {t.cases.cockpit.frequencyOverdue}
                        </p>
                        <p className="text-xs text-orange-800">
                          {t.cases.cockpit.frequencyOverdueBy.replace(
                            '{duration}',
                            frequencyOverdueBy
                          )}
                        </p>
                      </div>
                      {onSchedule && (
                        <button
                          type="button"
                          onClick={() => onSchedule()}
                          className="shrink-0 text-xs font-semibold text-orange-900 underline"
                        >
                          {t.cases.cockpit.scheduleCta}
                        </button>
                      )}
                    </div>
                  )}

                  {needsReport && (
                    <div className="flex items-center gap-2 rounded-lg border-l-2 border-amber-400 bg-amber-50 px-2.5 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-amber-900">
                          {t.cases.cockpit.reportMissing}
                        </p>
                        <p className="text-xs text-amber-800">
                          {t.cases.cockpit.reportMissingHint}
                        </p>
                      </div>
                      {canAddReport && (
                        <button
                          type="button"
                          onClick={onOpenAddReport}
                          className="shrink-0 text-xs font-semibold text-amber-900 underline"
                        >
                          {t.sessionReports.addReport}
                        </button>
                      )}
                    </div>
                  )}

                  {showConsentAlert && (
                    <div
                      className={`rounded-lg border-l-2 px-2.5 py-2 ${
                        pendingFile
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-amber-400 bg-amber-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon
                          className={`h-4 w-4 shrink-0 ${
                            pendingFile ? 'text-brand-700' : 'text-amber-700'
                          }`}
                        />
                        <p
                          className={`min-w-0 flex-1 text-xs font-medium ${
                            pendingFile ? 'text-brand-900' : 'text-amber-900'
                          }`}
                        >
                          {pendingFile
                            ? t.cases.consentPendingTitle
                            : t.cases.consentMissingTitle}
                        </p>
                        {!pendingFile && (
                          <button
                            type="button"
                            onClick={openFilePicker}
                            className="shrink-0 text-xs font-semibold text-amber-900 underline"
                          >
                            {t.cases.addConsent}
                          </button>
                        )}
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptAttr}
                        className="hidden"
                        onChange={handleFileChange}
                      />

                      {pendingFile && (
                        <div className="mt-2 space-y-2">
                          <p className="truncate text-xs text-brand-900">
                            <span className="font-medium">
                              {t.cases.consentSelectedFile}:
                            </span>{' '}
                            {pendingFile.name}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setConsentConfirmOpen(true)}
                              className="rounded-lg border border-brand-300 bg-white px-2.5 py-1.5 text-xs font-medium text-brand-700 transition hover:bg-brand-100"
                            >
                              {t.cases.consentUploadAction}
                            </button>
                            <button
                              type="button"
                              onClick={openFilePicker}
                              className="rounded-lg px-2 py-1.5 text-xs font-medium text-brand-800 underline"
                            >
                              {t.cases.consentChangeFile}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {pendingClarifySession !== null && (
                    <div className="flex items-center gap-2 rounded-lg border-l-2 border-slate-300 bg-slate-50 px-2.5 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-slate-700">
                          {t.cases.cockpit.clarifyPending.replace(
                            '{n}',
                            String(pendingClarifySession)
                          )}
                        </p>
                        <p className="text-xs text-slate-500">
                          {t.cases.cockpit.clarifyPendingHint}
                        </p>
                      </div>
                      {!blurSensitiveContent && (
                        <button
                          type="button"
                          onClick={() =>
                            openTablou({ caseId: caseItem.id, sessionNumber: pendingClarifySession })
                          }
                          className="shrink-0 text-xs font-semibold text-slate-700 underline"
                        >
                          {t.dashboard.todayFocus.openTablou}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="my-4 h-px bg-slate-100" />

          <div className="flex flex-wrap items-center gap-2">
            {canAddReport && (
              <Button size="sm" variant="primary" onClick={onOpenAddReport}>
                {t.cases.cockpit.addReportCta}
              </Button>
            )}
            {!hideMeetingNotes && canAddNote && (
              <Button size="sm" onClick={onAddNote}>
                {t.cases.cockpit.addNoteCta}
              </Button>
            )}
            {onSchedule && !nextAppointment && (
              <Button size="sm" icon={actionIcon.schedule} onClick={() => onSchedule()}>
                {t.cases.cockpit.scheduleCta}
              </Button>
            )}
            {showReports && (
              <Button
                size="sm"
                variant="ghost"
                icon={actionIcon.viewReports}
                onClick={onOpenReports}
              >
                {t.cases.cockpit.viewReportsCta}
              </Button>
            )}
            {onDeepen && (
              <Button
                size="sm"
                variant="ghost"
                icon={actionIcon.deepen}
                onClick={onDeepen}
                className="hidden sm:inline-flex"
              >
                {t.cases.cockpit.deepenCta}
              </Button>
            )}
            {onReassign && (
              <Button
                size="sm"
                variant={needsCaseReassignment(caseItem) ? 'danger' : 'ghost'}
                icon={actionIcon.reassign}
                onClick={onReassign}
              >
                {t.cases.reassign}
              </Button>
            )}
            {onEdit && (
              <Button size="sm" variant="ghost" icon={actionIcon.edit} onClick={onEdit}>
                {t.common.edit}
              </Button>
            )}
            {onCloseCase &&
              caseItem.status !== 'finished' &&
              caseItem.status !== 'unfinished' &&
              caseItem.status !== 'cancelled' && (
                <Button
                  size="sm"
                  variant="accent"
                  icon={actionIcon.closeCase}
                  onClick={onCloseCase}
                >
                  {t.caseClosure.closeCase}
                </Button>
              )}
            {onDelete && (
              <Button
                size="sm"
                variant="dangerGhost"
                icon={actionIcon.delete}
                onClick={onDelete}
              >
                {t.common.delete}
              </Button>
            )}

            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              className="ml-auto inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-xs font-medium text-brand-700 transition hover:bg-brand-50"
            >
              {expanded ? (
                <>
                  {t.cases.cockpit.hideDetails}
                  <ChevronUpIcon className="h-4 w-4" />
                </>
              ) : (
                <>
                  {t.cases.cockpit.showDetails}
                  <ChevronDownIcon className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>

        <ExpandCollapse open={expanded}>
          <div className="grid gap-3 rounded-b-xl border-t border-slate-200 bg-slate-50 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
              <div className="space-y-3">
                <DetailCard title={t.cases.cockpit.counseledPerson} icon={UserIcon}>
                  <DetailRow label={t.cases.age}>
                    {caseItem.age} {t.cases.years}
                  </DetailRow>
                  {translateSex(caseItem.sex, caseItem.age) && (
                    <DetailRow label={t.cases.sex}>
                      {translateSex(caseItem.sex, caseItem.age)}
                    </DetailRow>
                  )}
                  <DetailRow label={t.cases.civilStatusTitle}>
                    {translateCivilStatus(caseItem.civilStatus, caseItem.sex)}
                  </DetailRow>
                </DetailCard>

                <DetailCard title={t.cases.cockpit.aboutCase} icon={IdentificationIcon}>
                  <DetailRow label={t.cases.caseTitle}>{caseItem.title}</DetailRow>
                  <DetailRow label={t.cases.referralSource}>
                    {translateReferralSource(caseItem.referralSource)}
                  </DetailRow>
                  <DetailRow label={t.cases.createdLabel}>
                    {formatLongDate(caseItem.createdAt)}
                  </DetailRow>
                  {!blurSensitiveContent && canSeeConsent && (
                    <DetailRow label={t.cases.cockpit.consentLabel}>
                      {consentAttached ? (
                        <span className="font-medium text-emerald-700">
                          {caseItem.consentUploadedAt
                            ? t.cases.cockpit.consentAttachedOn.replace(
                                '{date}',
                                formatLongDate(caseItem.consentUploadedAt)
                              )
                            : t.cases.cockpit.consentAttachedNoDate}
                        </span>
                      ) : (
                        <span className="font-medium text-orange-700">
                          {t.cases.cockpit.consentNotAttached}
                        </span>
                      )}
                    </DetailRow>
                  )}
                  <button
                    type="button"
                    onClick={onOpenTimeline}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    <actionIcon.timeline className="h-4 w-4" aria-hidden="true" />
                    {t.cases.timeline}
                  </button>
                </DetailCard>
              </div>

              <div className="space-y-3">
                <DetailCard
                  title={t.cases.description}
                  icon={DocumentTextIcon}
                  action={
                    onEditDescription && !blurSensitiveContent ? (
                      <IconButton
                        icon={PencilSquareIcon}
                        label={t.cases.editDescription}
                        onClick={onEditDescription}
                      />
                    ) : undefined
                  }
                >
                  {blurSensitiveContent ? (
                    <div className="relative overflow-hidden rounded-lg bg-slate-50 p-3">
                      <p
                        className="select-none whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600 blur-[6px]"
                        aria-hidden
                      >
                        Conținut confidențial rezervat. Detaliile cazului nu sunt
                        afișate pentru acest rol. Text placeholder pentru efectul de
                        blur pe descrierea cazului.
                      </p>
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-3">
                        <p className="rounded-full bg-white/90 px-3 py-1 text-center text-xs font-medium text-slate-600 shadow-sm">
                          {t.adminTools.sensitiveContentRestricted}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600">
                        {isTruncated
                          ? `${description.substring(0, 150)}...`
                          : description || t.cases.noDescriptionProvided}
                      </p>
                      {isTruncated && (
                        <button
                          type="button"
                          onClick={onOpenDescription}
                          className="mt-2 text-sm font-medium text-brand-600 hover:text-brand-700"
                        >
                          {t.cases.viewFullDescription}
                        </button>
                      )}
                    </>
                  )}
                </DetailCard>

                {!hideMeetingNotes && (
                  <DetailCard
                    title={t.meetingNotes.latestMeetingNote}
                    icon={ChatBubbleBottomCenterTextIcon}
                  >
                    {latestNote ? (
                      <>
                        <p className="line-clamp-3 text-sm italic leading-relaxed text-slate-600">
                          {latestNote}
                        </p>
                        <button
                          type="button"
                          onClick={onOpenNotes}
                          className="mt-2 text-sm font-medium text-brand-600 hover:text-brand-700"
                        >
                          {t.meetingNotes.viewLatestNotes}
                        </button>
                      </>
                    ) : (
                      <p className="text-sm italic text-slate-500">
                        {t.meetingNotes.noMeetingNotesYet}
                      </p>
                    )}
                  </DetailCard>
                )}
              </div>
            </div>
        </ExpandCollapse>
      </article>

      <ConfirmDialog
        open={consentConfirmOpen}
        title={t.cases.consentConfirmTitle}
        message={t.cases.consentConfirmMessage}
        warningMessage={t.cases.consentConfirmWarning}
        confirmLabel={t.cases.consentUploadAction}
        variant="default"
        loading={uploadLoading}
        onClose={() => {
          if (!uploadLoading) setConsentConfirmOpen(false);
        }}
        onConfirm={handleConsentConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CaseListCard;
